/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { IconButton, NotificationBadge } from '@coursera/cds-core';
import { BellIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import IconButtonWrapper from 'bundles/composable-page-header/components/IconButtonWrapper';
import { DEFAULT_ICON_BUTTON_HEIGHT, DEFAULT_ICON_BUTTON_SVG_HEIGHT } from 'bundles/composable-page-header/constants';

import _t from 'i18n!nls/notification-center';

const styles = {
  badge: css`
    > .cds-notificationBadge-top {
      position: relative;
      left: -5px;
      top: 2px;
    }
  `,
};

type Props = {
  unreadCount: number;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isOpen?: boolean;
};

const NotificationIcon = ({ unreadCount, onClick, isOpen = false }: Props) => {
  const track = useTracker();
  const ariaText =
    unreadCount > 0 ? _t('Notifications, #{unreadCount} new notifications', { unreadCount }) : _t('Notifications');

  /* Tracking only if unread notifications are present. This reduces event noise and doesn't track if bell icon renders intermittently for logged out users */
  useEffect(() => {
    if (unreadCount > 0) {
      track('view_notifications_icon', {
        totalUnreadNotifications: unreadCount,
      });
    }
  }, [unreadCount, track]);

  /* Click icon handler with tracking */
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    track('click_notifications_icon', {
      totalUnreadNotifications: unreadCount,
    });
    onClick(e);
  };

  return (
    <NotificationBadge align="top" value={unreadCount} data-testid="unread-count" css={styles.badge}>
      <IconButtonWrapper
        iconButton={
          <IconButton
            onClick={(e) => handleClick(e)}
            size="small"
            aria-label={ariaText}
            tooltip={ariaText}
            variant="ghost"
            icon={<BellIcon size="large" color="support" />}
            aria-expanded={isOpen}
            data-testid="notification-icon-button"
          />
        }
        buttonSize={DEFAULT_ICON_BUTTON_HEIGHT}
        svgSize={DEFAULT_ICON_BUTTON_SVG_HEIGHT}
      />
    </NotificationBadge>
  );
};

export default NotificationIcon;
